/**
 * The Getting Started guide — ONE ordered list (TENANT_SETUP_SEQUENCE_GAPS W1–W11, session 331).
 *
 * It drives the guide modal, the dashboard banner and the step-complete toast. Before this there
 * were two copies (the modal's `CATEGORIES` and the context's `ORDERED_STEPS`) in different orders,
 * so the toast's "Next" and the guide disagreed about what to do next.
 *
 * Three stages, named for what the owner gets out of them, in the order the costs depend on each
 * other: you can't see a true cost until the wage, the ingredients and the packaging exist.
 * No stage gates another — every step still ticks itself from the data.
 *
 * ⚠️ Step KEYS are a contract: they are stored in `tenants.onboarding_skipped_steps`. Never rename
 * one; retire a key by removing its step (stored skips for it are then ignored).
 *
 * ⚠️ No `@/` runtime imports in this file: `frontend/scripts/check-onboarding-coverage.mjs` imports
 * it under Node, which cannot resolve the alias. Type-only imports are erased and are fine.
 *
 * ⚠️ The marketing site imports this file too (`site/src/lib/onboarding.ts`): the help center at
 * www.prophitable.io/help takes its stages, step labels and order from here, and each step's URL from its key.
 * Adding, removing or re-keying a step fails the site build until `site/src/content/help/` matches; a relabelled
 * step needs its article's wording checked (the H1 follows on its own).
 *
 * Copy rules (W5/W6): every `pitch` says what the owner GETS. A skippable step's pitch is a question
 * ("Charge sales tax? …") so it still reads true after they skip it — the pitch stays visible on a
 * skipped row. Every pitch was checked against what the app does; re-check before changing one.
 */
import type { OnboardingProgress } from '../services/onboarding';

export type OnboardingVisibilityKey =
  | 'showIngredients'
  | 'showMaterials'
  | 'showRecipes'
  | 'showVariants'
  | 'showPackages'
  | 'showMakeOrders';

/** Keys of `PLAN_FEATURE_REQUIREMENTS` / `UPGRADE_PROMPTS` — a step that needs a higher plan. */
export type OnboardingPlanFeature = 'customer-portal' | 'transport-orders';

export type OnboardingStepKey = Exclude<keyof OnboardingProgress, 'skippedSteps' | 'currentUserStaffId'>;

export interface OnboardingStepDef {
  key: OnboardingStepKey;
  label: string;
  /** The toast's line when the step's data first appears. */
  completedLabel: string;
  pitch: string;
  route: string | ((progress: Pick<OnboardingProgress, 'currentUserStaffId'>) => string);
  /** Hidden unless this production-config flag is on (co-pack hides what doesn't apply). */
  visibilityKey?: OnboardingVisibilityKey;
  /** Present ⇒ the step can be skipped, and this is the button's reason-shaped label (W4). */
  skipLabel?: string;
  /** Present ⇒ on a lower plan the row says GROWTH and opens the upgrade prompt instead (W8). */
  planFeature?: OnboardingPlanFeature;
}

export interface OnboardingStageDef {
  key: 'cost' | 'sell' | 'run';
  title: string;
  steps: OnboardingStepDef[];
}

export const ONBOARDING_STAGES: OnboardingStageDef[] = [
  {
    key: 'cost',
    title: 'Cost Your Products',
    steps: [
      {
        key: 'staffProfile',
        label: 'Add the wage of everyone who makes, packs or delivers',
        completedLabel: 'Wage added',
        // W11: true before AND after task 02 removes the $25/h fallback.
        pitch: "Your time is part of every product's cost. Until you add your wage, your costs can't count it correctly.",
        route: (p) => (p.currentUserStaffId ? `/staff/${p.currentUserStaffId}` : '/staff'),
      },
      {
        key: 'suppliers',
        label: 'Add your suppliers',
        completedLabel: 'Suppliers added',
        pitch: 'Every purchase order and scanned receipt is filed under who you bought it from.',
        route: '/suppliers',
      },
      {
        key: 'ingredients',
        label: 'Add your ingredients',
        completedLabel: 'Ingredients added',
        pitch: 'Set up your ingredient bill of materials',
        route: '/ingredients',
        visibilityKey: 'showIngredients',
      },
      {
        key: 'materials',
        label: 'Add your materials',
        completedLabel: 'Materials added',
        pitch: 'Set up the bill of materials for your product packaging',
        route: '/materials',
        visibilityKey: 'showMaterials',
      },
      {
        key: 'recipes',
        label: 'Add your recipes',
        completedLabel: 'Recipes added',
        pitch: 'Define the ingredients and process of making your recipes',
        route: '/recipes/new',
        visibilityKey: 'showRecipes',
      },
      {
        key: 'variants',
        label: 'Add variants',
        completedLabel: 'Variants added',
        pitch: 'Sell one recipe in several sizes or flavors? Each one gets its own cost.',
        // Variants are made on the recipe page; `/variants*` only redirect to `/recipes`.
        route: '/recipes',
        visibilityKey: 'showVariants',
        skipLabel: 'I make one size of each',
      },
      {
        key: 'packages',
        label: 'Add packaging',
        completedLabel: 'Packaging added',
        pitch: 'What an empty package costs you: the box, label and insert, plus the time to fold and assemble it.',
        route: '/packages/new',
        visibilityKey: 'showPackages',
      },
      {
        key: 'products',
        label: 'Finish your products',
        completedLabel: 'Products added',
        pitch: 'See your true cost and profit on every unit you sell.',
        route: '/products',
      },
    ],
  },
  {
    key: 'sell',
    title: 'Get Ready to Sell',
    steps: [
      {
        key: 'customers',
        label: 'Add your customers',
        completedLabel: 'Customers added',
        pitch: 'Their address is how delivery mileage gets priced on each order.',
        route: '/customers',
      },
      {
        key: 'companyProfile',
        label: 'Complete your company info',
        completedLabel: 'Company info completed',
        pitch: 'Delivery mileage is measured from here.',
        route: '/settings/company',
      },
      {
        key: 'postOffice',
        label: 'Add your post office',
        completedLabel: 'Post office saved',
        pitch: "Ship by mail? The round trip to the post office gets added to each shipped order's cost.",
        route: '/settings/shipping',
        skipLabel: "I don't ship by mail",
      },
      {
        key: 'taxSettings',
        label: 'Add your default sales tax',
        completedLabel: 'Sales tax saved',
        pitch: 'Charge sales tax? Set it once and every new sales order adds it.',
        route: '/settings/tax-rate',
        skipLabel: "I don't charge sales tax",
      },
      {
        key: 'priceLists',
        label: 'Set up price lists',
        completedLabel: 'Price list created',
        pitch: 'Charge some customers different prices? Give them a price list and their orders price themselves.',
        route: '/settings/price-lists',
        skipLabel: 'Everyone pays one price',
      },
      {
        key: 'orderOnline',
        label: 'Let customers order online',
        completedLabel: 'First customer invited to order online',
        pitch: 'Customers email or text you their orders? Send them a link instead. Their orders land in your Sales Orders at the cutoff.',
        // Ticks on the first portal invite (W8); the schedule comes first, so the step starts there.
        route: '/settings/ordering-schedule',
        skipLabel: "Customers don't order online",
        planFeature: 'customer-portal',
      },
    ],
  },
  {
    key: 'run',
    title: 'Run Your First Orders',
    steps: [
      {
        key: 'purchaseOrders',
        label: 'Create a purchase order',
        completedLabel: 'Purchase order created',
        pitch: 'When it arrives, receiving it adds the stock.',
        route: '/purchase-orders/new',
      },
      {
        key: 'production',
        label: 'Record your first production',
        completedLabel: 'Production recorded',
        pitch: 'Time a make order as you go, or record a whole week at once from what you paid your team. Each one brings your labor cost closer to what the work really takes.',
        route: '/make-orders',
        visibilityKey: 'showMakeOrders',
      },
      {
        key: 'completedSO',
        label: 'Complete a sales order',
        completedLabel: 'Sales order completed',
        pitch: 'See your real profit on an order, after delivery and fees.',
        route: '/sales-orders',
      },
      {
        key: 'completedTO',
        label: 'Complete a transport order',
        completedLabel: 'Transport order completed',
        pitch: 'Deliver several orders on one trip? The drive, fuel and driver time get split across them.',
        route: '/transport-orders',
        skipLabel: "I don't run delivery routes",
        planFeature: 'transport-orders',
      },
      {
        key: 'inviteStaff',
        label: 'Invite your team',
        completedLabel: 'Team invited',
        pitch: 'Want help? Supervisors run orders. Crew members time their work, so your costs use real times. Only you and your admins see costs and profit.',
        route: '/staff',
        skipLabel: "It's just me",
      },
    ],
  },
];
